import type { JSX } from "react";
import { useId } from "react";

/**
 * ⭐ **YÜKLEME GÖSTERGESİ — gövdeden bacaklara akan neon.**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 **DÖNEN DAİRE MARKAYA AİT DEĞİLDİ.**
 * ═══════════════════════════════════════════════════════════════════════════
 * Beklemenin yüzü, ürünün en çok görülen ekranıdır: yönetim konsolu, katılım
 * formu, lobi, sohbet geçmişi, rota geçişi. Oralarda **jenerik bir CSS dönen
 * dairesi** vardı (`border-top-color` + `rotate`) — herhangi bir yönetim
 * şablonundan çıkmış olabilirdi. Açılış ekranı ise marka işaretini ve sırayla
 * yanan devre düğümlerini gösteriyordu. Aynı eylem (bekle), iki bambaşka yüz.
 *
 * ### ⚠️ Neden `SpiderMark` doğrudan kullanılmadı?
 * O bir **logodur** ve 423×446'lık kutusunda ölçülmüş ince konturlar taşır.
 * 20 px'lik bir satır içi göstergede bacak kalınlığı 0.26 px'e düşer: tarayıcı
 * onu ya siler ya da grileştirir. Logoyu küçültmek logo tasarlamak değildir.
 * Bu yüzden geometri **gösterge için yeniden çizildi**: aynı dil (basamaklı
 * devre izi bacaklar, uçlarda açık halka, kalkan gövde), farklı ölçek
 * ekonomisi — kalın konturlar, az düğüm, 120'lik kare kutu.
 *
 * ### ⭐ Hareket: ışık **gövdeden uca** akar
 * Her bacak iki kez çizilir: sönük taban izi + üzerinde kısa bir **parlak
 * çizgi**. Parlak çizgi `stroke-dashoffset` ile iz boyunca yürür ve her bacak
 * sırayla gecikmelidir; sonuç, gövdeden dışa doğru yayılan bir nabızdır.
 * Bekleme "dönen bir şey" değil, "sistemin damarlarında akan bir sinyal"
 * olarak okunur.
 *
 * ⚠️ Hareket **CSS'tedir**, SMIL değil: `prefers-reduced-motion` ile SMIL
 *    durdurulamaz ve vestibüler rahatsızlığı olan kullanıcı kaçamaz. Azaltılmış
 *    hareket tercihinde akış durur, yerine çok yavaş bir nefes alma kalır —
 *    çünkü göstergenin hâlâ "çalışıyor" demesi gerekir.
 *
 * ⚠️ Gradyan/filtre kimliği `useId()` ile üretilir: sayfada iki gösterge
 *    birden çizilirse (konsolda tablo + kart) sabit bir `id` çakışır ve
 *    ikincisi birincinin filtresini kullanır.
 */

/**
 * ⭐ Bacak geometrisi **tek kaynaktır**: taban izi, parlak çizgi ve ayna
 *    görüntüsü aynı diziden çizilir. Üç yere kopyalansaydı bir düzeltme daima
 *    yalnız birine uygulanır ve gösterge yamuk kalırdı.
 *
 * ⚠️ Uzunluklar bilerek **birbirine yakın** tutuldu (~62–70 birim): parlak
 *    çizgi tek bir `stroke-dasharray` ile sürülür ve uzunluklar çok farklı
 *    olsaydı kısa bacakta ışık koşar, uzun bacakta sürünürdü.
 */
const LEGS: readonly string[] = Object.freeze([
  "M68,52 L82,40 L92,22",
  "M70,57 L86,57 L96,47 L110,47",
  "M70,64 L86,64 L101,74",
  "M68,69 L82,81 L92,99",
]);

/** Bacak uçlarındaki açık halkalar — logodaki devre pad'i dilinin karşılığı. */
const TIPS: readonly { cx: number; cy: number }[] = Object.freeze([
  { cx: 92, cy: 22 },
  { cx: 110, cy: 47 },
  { cx: 101, cy: 74 },
  { cx: 92, cy: 99 },
]);

export function SpiderLoader(props: { size?: number }): JSX.Element {
  const uid = useId();
  const glowId = `glow-${uid}`;
  const size = props.size ?? 96;

  const side = (mirror: boolean): JSX.Element => (
    <g key={mirror ? "l" : "r"} {...(mirror ? { transform: "scale(-1,1) translate(-120,0)" } : {})}>
      {/* Taban izi — ışık geçmediğinde de bacak GÖRÜNÜR kalır. */}
      <g data-loader-trace>
        {LEGS.map((d) => (
          <path key={d} d={d} />
        ))}
        {TIPS.map((t) => (
          <circle key={`${String(t.cx)}-${String(t.cy)}`} cx={t.cx} cy={t.cy} r={4} />
        ))}
      </g>

      {/*
        ⚠️ Parlak çizgi AYRI bir katmandır ve taban izinin üstünde çizilir.
           Tek katmanda renk değiştirerek yapılsaydı ışık geçmeyen bacak
           tamamen kaybolur, gösterge titrek görünürdü.
        ⚠️ `--i` her bacağın sırasıdır; gecikme CSS'te ondan hesaplanır.
           Satır içi `animation-delay` yazmak, hareketi CSS'in
           `prefers-reduced-motion` kapısının dışına çıkarırdı.
      */}
      <g data-loader-pulse filter={`url(#${glowId})`}>
        {LEGS.map((d, i) => (
          <path key={d} d={d} style={{ ["--i" as string]: String(mirror ? i + 4 : i) }} />
        ))}
      </g>
    </g>
  );

  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      data-spider-loader
      role="presentation"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth={4.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <defs>
        {/*
          ⚠️ Parlama `filter` iledir, `box-shadow` değil: SVG konturuna gölge
             veren tek yol budur. Filtre **grup düzeyindedir** — sekiz ayrı
             yola verilseydi tarayıcı sekiz ayrı geçici yüzey ayırırdı.
        */}
        <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="2.2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {side(false)}
      {side(true)}

      {/*
        ⭐ Gövde: logonun kalkanı ve ondan **ayrı duran** baş noktası. Ayrı
           durması işareti "örümcek" yapan şeylerden biridir; birleştirmek
           gösterge ölçeğinde onu bir damlaya çevirirdi.
      */}
      <g data-loader-body>
        <circle cx="60" cy="43" r="4.5" />
        <path d="M60,50 L49,55 C49,67 52,74 60,79 C68,74 71,67 71,55 Z" />
      </g>
    </svg>
  );
}
